import type { Strings } from '../index.ts';
import { unreadable } from './shared.ts';

/**
 * Alles, was über eine Playlist gesagt wird, die dem Leser **gehört**.
 *
 * Der Sichtbarkeitsteil ist der, der zweimal gelesen werden will: „nicht gelistet“ und „privat“ kommen hier
 * als derselbe Wert an, eine Playlist, die diese Sitzung nicht selbst geschrieben hat, ist also entweder
 * *sicher* öffentlich oder *eine von* den anderen zwei. Jeder Satz unten ist auf diese Form geschrieben,
 * weil der Unterschied darüber entscheidet, ob ein verschickter Link bei anderen funktioniert.
 *
 * `title.bytes` zählt **Bytes** und nicht Zeichen, und im Deutschen ist das kein Kleingedrucktes: „76
 * Zeichen“ liegt mit Umlauten über dem Limit, mit dem der englische Text noch darunter liegt. Die Einheit
 * bleibt deshalb im Satz.
 */
export const playlists: Strings['playlists'] = {
  visibility: {
    public: 'Öffentlich',
    unlisted: 'Nicht gelistet',
    private: 'Privat',
    notPublic: 'Nicht öffentlich',
    detail: {
      public: 'Auf deinem Kanal gelistet und findbar.',
      unlisted: 'Nicht gelistet, aber wer den Link hat, kann sie öffnen.',
      private: 'Nur du, angemeldet. Ein Link funktioniert für niemanden sonst.',
    },
    unknownCaveat:
      'Nicht gelistet und privat sehen von hier aus gleich aus, Visious kann also nur sagen, dass es eines von beiden ist. Es neu zu setzen macht es eindeutig.',
  },

  untitled: 'Playlist ohne Titel',

  video: { one: 'Video', other: 'Videos' },

  title: {
    missing: 'Eine Playlist braucht einen Titel.',
    bytes: (used, max) => `${used} von ${max} Bytes`,
    bytesOver: (used, max) => `${used} von ${max} Bytes. Der Rest wird abgeschnitten`,
    missingProblem: {
      title: 'Eine Playlist braucht einen Titel',
      detail:
        'Die Instanz würde einen leeren annehmen, und heraus käme eine Zeile, auf die man nicht klicken kann. Visious lehnt das stattdessen ab.',
      retryable: false,
    },
  },

  membership: {
    present: 'Schon in dieser Playlist',
    unknown: 'Lange Playlist. Visious kann nicht sagen, ob es schon darin ist',
  },

  problem: {
    notFound: {
      title: 'Diese Playlist ist weg',
      detail:
        'Eine Playlist, die woanders gelöscht wurde, auf der Instanz selbst oder in einem anderen Tab, liest sich genau so. Die Liste neu zu laden räumt das auf.',
      retryable: true,
    },
    unreadable: unreadable('Deine Playlists konnten nicht gelesen werden'),
  },

  page: {
    heading: 'Playlists',
    lede: 'Die Playlists in deinem Instanz-Konto.',
    loading: 'Deine Playlists werden geladen…',
    listLabel: 'Deine Playlists',
    empty: {
      title: 'Noch keine Playlists',
      detail:
        'Unter diesem Konto gibt es keine. Oben eine anzulegen genügt, oder füge von einer Watch-Seite aus ein Video zu einer neuen Playlist hinzu.',
    },
    create: {
      sectionLabel: 'Playlist anlegen',
      heading: 'Neue Playlist',
      titleLabel: 'Titel',
      titlePlaceholder: 'Autofahrt',
      privacyLabel: 'Wer sie sehen kann',
      submit: 'Anlegen',
      submitting: 'Wird angelegt…',
    },
    updated: (when) => `Aktualisiert ${when}`,

    manage: {
      enter: 'Verwalten',
      leave: 'Fertig',
      selectLabel: (title) => `${title} auswählen`,
      selectedCount: (count) => `${count} ausgewählt`,
      clearSelection: 'Auswahl aufheben',
      deleteSelected: 'Löschen',
      makePublic: 'Öffentlich machen',
      makeUnlisted: 'Nicht listen',
      makePrivate: 'Privat machen',
      working: (done, total) => `Läuft… ${done} von ${total}`,
      confirmDetail:
        'Löschen entfernt sie aus deinem Konto auf der Instanz. Es gibt kein Zurück. Die Videos selbst bleiben unberührt, aber die Listen sind weg.',
      failed: {
        title: 'Einige Änderungen wurden abgelehnt',
        detail:
          'Die Instanz hat einen Teil der Änderung nicht angenommen. Die Liste unten zeigt, was sie jetzt hält, und den Rest auszuwählen und es erneut zu versuchen ist unbedenklich.',
        retryable: true,
      },
    },
  },

  add: {
    open: 'In Playlist speichern',
    addTo: (title) => `${title} in einer Playlist speichern`,
    close: 'Schließen',
    signedOutBefore:
      'Playlists liegen in deinem Konto auf der Instanz, das braucht dich also angemeldet.',
    signIn: 'Anmelden',
    signedOutAfter: 'und du kommst direkt hierher zurück.',
    loading: 'Deine Playlists werden geladen…',
    none: 'Du hast noch keine Playlists. Die erste ist einen Titel entfernt.',
    createSubmit: 'Anlegen und speichern',
    added: 'Hinzugefügt',
    failed: 'Fehlgeschlagen',
  },

  watchLater: {
    pill: 'Später ansehen',
    addTitle: 'Zu „Später ansehen“ hinzufügen',
    inPill: 'In „Später ansehen“',
    removeTitle: 'Aus „Später ansehen“ entfernen',
    add: (title) => `${title} zu „Später ansehen“ hinzufügen`,
    remove: (title) => `${title} aus „Später ansehen“ entfernen`,
    working: 'Wird gespeichert…',
    pickerTitle: 'Wähle deine „Später ansehen“-Playlist',
    pickerLede:
      '„Später ansehen“ speichert in eine gewöhnliche Playlist in deinem Konto. Wähle welche, oder lege unten eine frische an.',
    close: 'Schließen',
    current: 'Aktuell',
    createSubmit: 'Anlegen und verwenden',
    loading: 'Deine Playlists werden geladen…',
    none: 'Du hast noch keine Playlists. Die erste ist einen Titel entfernt.',
    signedOutBefore:
      '„Später ansehen“ speichert in eine Playlist in deinem Konto, das braucht dich also angemeldet.',
    signIn: 'Anmelden',
    signedOutAfter: 'und du kommst direkt hierher zurück.',
  },

  /*
   * Kurze Etiketten statt Verbalphrasen: sie stehen in `write.badBody` in einer Klammer hinter dem Satz
   * („Die Instanz hat diese Änderung abgelehnt (Playlist anlegen)“), und dort liest sich ein „beim
   * Anlegen einer Playlist“ wie ein abgebrochener Nebensatz.
   */
  action: {
    create: 'Playlist anlegen',
    update: 'Änderungen speichern',
    delete: 'Playlist löschen',
    add: 'Video hinzufügen',
    remove: 'Video entfernen',
  },

  write: {
    badBody: (action) => ({
      title: `Die Instanz hat diese Änderung abgelehnt (${action})`,
      detail:
        'Visious hat ihr etwas geschickt, das sie nicht annimmt. Es wurde nichts geändert, und eine neue Anmeldung hilft nicht. Das ist ein Fehler hier und kein Problem mit deinem Konto.',
      retryable: false,
    }),
    playlistGone: {
      title: 'Diese Playlist ist weg',
      detail:
        'Entweder wurde sie woanders gelöscht, oder es ist eine YouTube-Playlist, und die lassen sich von hier aus nicht ändern.',
      retryable: false,
    },
    entryGone: {
      title: 'Dieser Eintrag ist nicht mehr da',
      detail:
        'Er steht nicht mehr in der Playlist, meist weil er woanders schon entfernt wurde. Die Playlist neu zu laden behebt das.',
      retryable: true,
    },
    videoGone: {
      title: 'Dieses Video lässt sich nicht hinzufügen',
      detail:
        'YouTube liefert es nicht, es gibt also nichts, was in die Playlist könnte. Ein gelöschtes, privates oder regionsgesperrtes Video liest sich so.',
      retryable: false,
    },
  },
};
